import { useState, useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import seoKeywordsService from '@/services/seoKeywords';
import seoCopilotService from '@/services/seoCopilot';
import { FetchApiIds } from '@/types';
import { SeoCopilotSession, SeoCopilotCycle, SeoTrackedKeyword } from '@/types/api/seo';
import SeoCopilotComponent from './SeoCopilot.component';

type KeywordsPaginated = { data: SeoTrackedKeyword[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null };

const SeoCopilotPage = function (): JSX.Element {
  const { project } = useSeoProject();
  const projectId = project?.id ?? '';

  const { data: sessions = [], isLoading, refetch } = useSeoFetch<SeoCopilotSession[]>(
    ['seo-copilot-sessions', projectId],
    () => seoCopilotService.getSessions(projectId),
    { enabled: !!projectId }
  );

  const { data: keywordsData } = useSeoFetch<KeywordsPaginated>(
    [FetchApiIds.SEO_KEYWORDS, projectId],
    () => seoKeywordsService.getAll({ projectId, page: 1, take: 500 }),
    { enabled: !!projectId }
  );

  const keywords = keywordsData?.data ?? [];

  const enrichedSessions = sessions.map((s) => ({
    ...s,
    keyword: keywords.find((k) => k.id === s.keywordId),
  }));

  // Cycles cache — loaded on demand when user expands a session
  const [cyclesMap, setCyclesMap] = useState<Record<string, SeoCopilotCycle[]>>({});

  const handleLoadCycles = useCallback(async (sessionId: string) => {
    if (cyclesMap[sessionId]) return; // Already loaded
    try {
      const cycles = await seoCopilotService.getCycles(sessionId);
      setCyclesMap((prev) => ({ ...prev, [sessionId]: cycles }));
    } catch (err) {
      console.error('[SEOx] Failed to load cycles:', err);
      setCyclesMap((prev) => ({ ...prev, [sessionId]: [] })); // Set empty to stop loading
    }
  }, [cyclesMap]);

  const handlePause = useCallback(async (sessionId: string) => {
    await seoCopilotService.pauseSession(sessionId);
    refetch();
  }, [refetch]);

  const handleResume = useCallback(async (sessionId: string) => {
    await seoCopilotService.resumeSession(sessionId);
    refetch();
  }, [refetch]);

  return (
    <SeoCopilotComponent
      sessions={enrichedSessions}
      cyclesMap={cyclesMap}
      isLoading={isLoading}
      onPause={handlePause}
      onResume={handleResume}
      onLoadCycles={handleLoadCycles}
    />
  );
};

export default SeoCopilotPage;
